// src/components/PriceFeatures.tsx
import dashboard from '../../assets/images/features/dashboard.png';
import Image from './Image';
import { Link } from 'react-router-dom';

import React from 'react';

const features = [
  {
    title: '100% Hands On Learning, No More Boring Videos',
    description: (
      <>
        Write real code in every session, instead of passively watching someone
        else. This makes coding fun and prevents the boredom and disengagement
        that often comes with purely video based courses.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Hands-on coding learning illustration',
  },
  {
    title:
      "Less Frustration & More Learning Moments with My Code Dragon AI's Feedback & Support",
    description: (
      <>
        Getting stuck on an error can be frustrating. That's where My Code
        Dragon's AI-powered assistant comes in, it's like a personal coding
        tutor available 24/7.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'AI support illustration',
  },
  {
    title: 'Ever-Growing Course Catalog',
    description: (
      <>
        Offering self-paced courses in Python, R, SQL, JavaScript, and HTML &
        CSS. In total, there are 1,300+ interactive coding activities, with more
        on the way.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Course catalog illustration',
  },
  {
    title: 'Typing Foundations for Coding Success Course',
    description: (
      <>
        My Code Dragon offers a dedicated Typing course to help new typers
        improve their typing speed and accuracy, especially with those tricky
        symbols used in coding.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Typing course illustration',
  },
  {
    title: 'Dragon Mastery Medals',
    description: (
      <>
        Our Dragon Mastery Medals reward students for their performance in each
        course. They'll aim to debug their code thoroughly and use the AI
        assistance more sparingly to reach Gold Dragon Mastery level accuracy
        and independence.
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Dragon Mastery Medals illustration',
  },
  {
    title: 'Progress Tracking & Personalized Dashboard',
    description: (
      <>
        Each student gets a personalized dashboard that visualizes their
        accomplishments, stats, and personalized feedback. It's easy for kids
        and parents to see progress at a glance, making it simple to stay
        motivated and on track with coding goals:
      </>
    ),
    imageSrc: dashboard,
    imageAlt: 'Progress tracking dashboard illustration',
  },
];

interface FeatureCardProps {
  title: string;
  description: string | React.ReactNode;
  imageSrc: string;
  imageAlt: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
}) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
    <h3 className="text-lg font-bold mb-3 text-[#023f81] leading-tight">
      {title}
    </h3>
    <div className="flex items-start space-x-3">
      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg
          className="w-4 h-4 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const PriceFeatures: React.FC = () => (
  <div className="mx-4 md:mx-8 lg:mx-16 xl:mx-40 p-4 md:p-6">
    <div className="text-[1.2rem] md:text-[1.3rem] lg:text-[1.5rem] font-bold text-center flex justify-center mb-8 md:mb-12">
      Why My Code Dragon?
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
      {features.map((feature, index) => (
        <FeatureCard
          key={index}
          title={feature.title}
          description={feature.description}
          imageSrc={feature.imageSrc}
          imageAlt={feature.imageAlt}
        />
      ))}
    </div>

    {/* Promotional Section */}
    <div className="mt-16 text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-black mb-6">
        Unlock Your Child's Coding Potential - Enroll Now
      </h2>
      <p className="text-lg text-black mb-8 max-w-3xl mx-auto">
        Spark a lifelong passion for technology & innovation. Don't miss this
        chance to unlock your child's coding potential and set them on the path
        to success.
      </p>
      <Link
        to="/signup"
        className="inline-block text-teal-600 italic underline text-lg font-medium hover:text-teal-800 transition-colors"
      >
        Get Started Today
      </Link>
    </div>
  </div>
);

export default PriceFeatures;
